import * as React from 'react';
import { Control, FieldValues, Controller, ControllerRenderProps } from 'react-hook-form';
import Select, { MultiValue } from 'react-select';
import { DefaultSelectProps, ISelectOption, UskinnedSelectStyles } from '../models/select';
import { DatePicker, IDatePickerDefaultValue } from './date-picker';
import { ErrorMessage } from './error-message';
import { ITimePickerDefaultValue, TimePicker } from './time-picker';

export interface IFieldsetFactoryForm {
  name: string;
  control: Control<FieldValues>;
}

export enum FieldsetFactoryType {
  DatePicker = 'date-picker',
  TimePicker = 'time-picker',
  Select = 'select'
}

export interface IFieldsetFactoryValidations {
  validate: { [key: string]: (value: string) => boolean | string };
}

interface IBaseFieldsetFactoryProps {
  label: string;
  name: string;
  type?: FieldsetFactoryType;
  form: IFieldsetFactoryForm;
  required?: boolean;
  validations?: IFieldsetFactoryValidations;
  disabled?: boolean;
}

interface IDatePickerFieldsetFactoryProps extends IBaseFieldsetFactoryProps {
  type: FieldsetFactoryType.DatePicker;
  defaultValue?: IDatePickerDefaultValue;
}

interface ITimePickerFieldsetFactoryProps extends IBaseFieldsetFactoryProps {
  type: FieldsetFactoryType.TimePicker;
  defaultValue?: ITimePickerDefaultValue;
}

interface ISelectFieldsetFactoryProps extends IBaseFieldsetFactoryProps {
  type: FieldsetFactoryType.Select;
  defaultValue?: string;
  selectProps: DefaultSelectProps;
}

export type IFieldsetFactoryProps =
  | IDatePickerFieldsetFactoryProps
  | ITimePickerFieldsetFactoryProps
  | ISelectFieldsetFactoryProps;

export const FieldsetFormFactory = (props: IFieldsetFactoryProps) => {
  const { label, name, required = false, form, type = '', validations = {}, defaultValue, disabled = false } = props;
  const renderController = () => (
    <Controller
      render={({ field, fieldState }) => (
        <>
          {renderInput(field)}
          <ErrorMessage label={label} error={fieldState.error} />
        </>
      )}
      control={form.control}
      name={name}
      defaultValue={defaultValue}
      rules={{
        required,
        ...validations
      }}
    />
  );

  const renderInput = React.useCallback(
    (field: ControllerRenderProps) => {
      const { selectProps } = props as ISelectFieldsetFactoryProps;
      switch (type) {
        case FieldsetFactoryType.DatePicker:
          return (
            <DatePicker
              defaultValue={defaultValue as IDatePickerDefaultValue}
              onChange={newValue => {
                field.onChange(newValue);
              }}
              disabled={disabled}
            />
          );
        case FieldsetFactoryType.TimePicker:
          return (
            <TimePicker
              defaultValue={defaultValue as ITimePickerDefaultValue}
              onChange={newValue => {
                field.onChange(newValue);
              }}
              disabled={disabled}
            />
          );
        case FieldsetFactoryType.Select:
          return (
            <Select
              {...(selectProps || {})}
              onChange={(newValue: ISelectOption | MultiValue<ISelectOption>) => {
                if (selectProps && selectProps.isMulti) {
                  field.onChange(newValue);
                } else {
                  field.onChange((newValue as ISelectOption).value);
                }
              }}
              styles={UskinnedSelectStyles}
              isDisabled={disabled}
            />
          );
        default:
          return null;
      }
    },
    [type]
  );

  return (
    <fieldset className='row form-group fieldset-form' aria-required={required}>
      <legend className='col col-12 col-md-3 col-lg-2 text-left text-md-right form-label'>
        <div className='mr-0 mr-md-3'>
          {label}
          {required ? <em className='req ml-2'>*</em> : ''}
        </div>
      </legend>
      <div className='col col-12 col-md-9 col-lg-10'>{renderController()}</div>
    </fieldset>
  );
};
